import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import {
  ImgSanity,
  type SanityTextContentProps,
  SanityTextRenderer,
} from '@slite/components'
import { ChevronLeftIcon } from '@slite/icons'

import { WebsiteCard } from '../../../components/Card/Card'
import { Section } from '../../../components/Section'
import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import BlogLinks from '../Links'
import type { Data } from './+data'

export default function BlogShowPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { page: data, relatedArticles } = useData<Data>()
  const content = data.content as SanityTextContentProps
  const bioContent = data.author.bio as SanityTextContentProps

  return (
    <SlitePage
      background="shade-100"
      canonicalUrl={routes.blog.post({ slug: data.slug.current })}
      footerBackground="moon-dust"
      description={data.metaDescription ?? undefined}
      ogDescription={data.openGraphDescription ?? ''}
      title={data.title}
      ogTitle={data.openGraphTitle ?? data.title}
      ogImage={
        data.openGraphImage?.url ??
        'https://cdn.sanity.io/images/dnsgqii5/production/a2df3c37ffc5a2d9dd8383c981d3e4e2cc7c4bcf-2400x1254.jpg'
      }
    >
      <Section background="shade-100">
        <div className="grid-12">
          <div className="grid-span-8 grid-offset-2 grid-span-12-small grid-offset-0-small">
            <div className="text-align-center margin-bottom-40">
              <a
                href={routes.blog.index()}
                className="flexbox flexbox-centered flexbox-column-gap-8 shade-10 margin-bottom-20"
              >
                <ChevronLeftIcon size={12} />
                <span>
                  <FormattedMessage
                    defaultMessage="All articles"
                    id="blog.show.allArticlesLink"
                  />
                </span>
              </a>
              <WebsiteText
                variant="title"
                className="line-height-large"
                htmlTag="h1"
              >
                {data.title}
              </WebsiteText>
            </div>
          </div>

          {data.thumbnailImage && (
            <div className="grid-span-12">
              <ImgSanity
                src={data.thumbnailImage?.url}
                alt={data.title}
                className="margin-bottom-40 radius-larger"
                priority
              />
            </div>
          )}

          <div className="grid-span-8 grid-offset-2 grid-span-10-small grid-offset-1-small simple-rich-text simple-rich-text-sanity large">
            <SanityTextRenderer variant="website" content={content} />
          </div>
        </div>
      </Section>
      <SectionCurve
        from="moon-dust"
        to="shade-100"
        shape="top-large"
        style={{ transform: 'rotate(180deg) translateY(1px)' }}
      />
      <Section background="moon-dust">
        <div className="grid-12 grid-row-gap-60">
          <div className="grid-span-3 grid-span-12-small">
            <div className="margin-bottom-32">
              <WebsiteText
                variant="smallUppercase"
                className="font-size-8 letter-spacing-small margin-bottom-8"
                style={{ color: 'rgba(0,0,0,0.5)' }}
              >
                <FormattedMessage
                  defaultMessage="Last updated"
                  id="blog.show.lastUpdated"
                />
              </WebsiteText>
              <WebsiteText variant="body">
                {intl.formatDate(data.date, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </WebsiteText>
            </div>
            <div>
              <WebsiteText
                variant="smallUppercase"
                className="font-size-8 letter-spacing-small margin-bottom-8"
                style={{ color: 'rgba(0,0,0,0.5)' }}
              >
                <FormattedMessage
                  defaultMessage="Share this story"
                  id="blog.show.shareThisStory"
                />
              </WebsiteText>
              <BlogLinks title={data.title} slug={data.slug.current} />
            </div>
          </div>
          <div className="grid-span-9 grid-span-12-small">
            <div className="flexbox flexbox-align-center flexbox-column-gap-24 margin-bottom-60">
              <ImgSanity
                alt={data.author.name}
                src={data.author.picture.url}
                style={{ width: 80 }}
                className="radius-round"
              />
              <div>
                <WebsiteText
                  variant="smallUppercase"
                  className="font-size-8 letter-spacing-small margin-bottom-8"
                  style={{ color: 'rgba(0,0,0,0.5)' }}
                >
                  <FormattedMessage
                    id="blog.show.writtenBy"
                    defaultMessage="Written by {authorName}"
                    values={{
                      authorName: data.author.name,
                    }}
                  />
                </WebsiteText>
                <div className="simple-rich-text simple-rich-text-sanity">
                  <SanityTextRenderer variant="website" content={bioContent} />
                </div>
              </div>
            </div>
            <div className="margin-bottom-16">
              <h4 className="font-family-title font-size-5 font-weight-medium margin-bottom-16">
                <FormattedMessage
                  defaultMessage="Related content"
                  id="blog.show.relatedContent"
                />
              </h4>
              <div className="flexbox flexbox-column flexbox-row-gap-16">
                {relatedArticles.map((article) => (
                  <WebsiteCard
                    key={article._id}
                    className="padding-horizontal-12 padding-vertical-12 flexbox flexbox-column-gap-32 flexbox-align-center"
                    href={article.slug.current}
                  >
                    <ImgSanity
                      alt={article.title}
                      className="radius-large"
                      style={{ width: '20%' }}
                      src={
                        article.thumbnailImage?.url ??
                        article.openGraphImage?.url ??
                        ''
                      }
                    />
                    <div>
                      <h5 className="font-size-6 font-family-title font-weight-medium margin-bottom-4 shade-00 shade-10-on-hover">
                        {article.title}
                      </h5>
                      <p className="shade-20 font-size-7">
                        <FormattedMessage
                          defaultMessage="By {authorName}"
                          id="blog.show.relatedContent.authorName"
                          values={{ authorName: data.author.name }}
                        />
                      </p>
                    </div>
                  </WebsiteCard>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>
      <SectionLastCTA background="moon-dust" />
    </SlitePage>
  )
}
